{
  "$schema": "https://cogo.xyz/schemas/webmcp-manifest.v1.json",
  "version": "1.0.0",
  "generatedAt": "2026-04-21T00:00:00.000Z",
  "source": {
    "kind": "studio-manifest",
    "description": "Studio cross-shell agentic tools for Phase 4 (menu-restructure)."
  },
  "persona": "studio",
  "narrativeContext": "You are the Studio Assistant — a cross-shell helper that can navigate between Dashboard, Commerce, and IDE shells, open or create projects, and make scoped edits. Read-only tools (navigation, context introspection) auto-approve. Write tools (project creation, block edits, commerce mutations) require explicit user confirmation before firing.",
  "tools": [
    {
      "name": "cogo.nav.goto",
      "description": "Navigate to a Studio route. Optional projectId opens a project in IDE shell. Read-only destination change — no content is mutated.",
      "inputSchema": {
        "type": "object",
        "properties": {
          "route": {
            "type": "string",
            "description": "StudioRoute value such as 'home', 'projects', 'templates', 'settings', 'commerce', 'products', 'orders', 'revenue', 'members', 'billing', 'analytics', 'design-system', 'map'."
          },
          "projectId": {
            "type": "string",
            "description": "Optional. If present, the IDE shell is opened on this project instead of the dashboard route."
          }
        },
        "required": ["route"]
      },
      "metadata": {
        "readOnly": true,
        "tags": ["navigation"],
        "persona": "all"
      }
    },
    {
      "name": "cogo.nav.currentContext",
      "description": "Return the user's current Studio page context — shell (dashboard/commerce/ide), route, project (if any), and active block (IDE only). Useful when the assistant needs to confirm 'where am I?' before acting.",
      "inputSchema": {
        "type": "object",
        "properties": {}
      },
      "metadata": {
        "readOnly": true,
        "tags": ["navigation", "introspection"],
        "persona": "all"
      }
    },
    {
      "name": "cogo.project.create",
      "description": "Create a new Studio project. Opens the New Project wizard prefilled with the provided name and optional template. The user completes the wizard.",
      "inputSchema": {
        "type": "object",
        "properties": {
          "name": { "type": "string", "description": "Project display name." },
          "templateId": {
            "type": "string",
            "description": "Optional Studio template id to seed the new project."
          }
        },
        "required": ["name"]
      },
      "metadata": {
        "readOnly": false,
        "tags": ["project", "write"],
        "persona": "dashboard"
      }
    },
    {
      "name": "cogo.project.open",
      "description": "Open an existing Studio project by id. Switches the app into IDE shell and loads the project blocks.",
      "inputSchema": {
        "type": "object",
        "properties": {
          "projectId": {
            "type": "string",
            "description": "Supabase project id."
          }
        },
        "required": ["projectId"]
      },
      "metadata": {
        "readOnly": true,
        "tags": ["project", "navigation"],
        "persona": "all"
      }
    },
    {
      "name": "cogo.block.edit",
      "description": "Apply a JSON-patch-style update to a block in the currently-open project. Gated to IDE shell — the tool refuses if no project is loaded. Phase 4 stub: the patch is recorded but not applied yet.",
      "inputSchema": {
        "type": "object",
        "properties": {
          "blockName": {
            "type": "string",
            "description": "Block id (e.g. 'home-hero')."
          },
          "patch": {
            "type": "object",
            "description": "Partial update to merge into the block's manifest / ui. Phase 4 treats this as opaque payload."
          }
        },
        "required": ["blockName", "patch"]
      },
      "metadata": {
        "readOnly": false,
        "tags": ["block", "write"],
        "persona": "ide"
      }
    },
    {
      "name": "cogo.page.updateElement",
      "description": "Update a selected Page element through the same cogo.studio.updateElement MCP mutation used by direct Page controls. Gated to IDE shell.",
      "inputSchema": {
        "type": "object",
        "properties": {
          "blockName": {
            "type": "string",
            "description": "Optional block id. Defaults to the selected or active Page block."
          },
          "elementId": {
            "type": "string",
            "description": "Optional element id. Defaults to the currently selected Page element."
          },
          "changes": {
            "type": "object",
            "description": "Element field patch, such as {\"text\":\"New label\"} or {\"className\":\"...\"}."
          }
        },
        "required": ["changes"]
      },
      "metadata": {
        "readOnly": false,
        "tags": ["page", "element", "write"],
        "persona": "ide"
      }
    },
    {
      "name": "cogo.actionflow.bindEvent",
      "description": "Bind a Page element event to an ActionFlow through the same event-binding MCP mutation used by the ActionFlow and Page UI. Gated to IDE shell.",
      "inputSchema": {
        "type": "object",
        "properties": {
          "blockName": {
            "type": "string",
            "description": "Optional block id. Defaults to the selected or active Page block."
          },
          "elementId": {
            "type": "string",
            "description": "Optional element id. Defaults to the selected Page element."
          },
          "eventType": {
            "type": "string",
            "description": "Event type such as tap or change. Defaults to tap."
          },
          "flowName": {
            "type": "string",
            "description": "ActionFlow name to bind."
          },
          "flow": { "type": "string", "description": "Alias for flowName." }
        },
        "required": ["flowName"]
      },
      "metadata": {
        "readOnly": false,
        "tags": ["actionflow", "event", "write"],
        "persona": "ide"
      }
    },
    {
      "name": "cogo.ontology.bindSelectedPageFlow",
      "description": "Run the Project Ontology work-queue repair that binds the selected Page element to a named ActionFlow. It opens the Project Ontology rail and then uses the same event-binding MCP mutation as ActionFlow.",
      "inputSchema": {
        "type": "object",
        "properties": {
          "blockName": {
            "type": "string",
            "description": "Optional block id. Defaults to the selected or active Page block."
          },
          "elementId": {
            "type": "string",
            "description": "Optional element id. Defaults to the selected Page element."
          },
          "eventType": {
            "type": "string",
            "description": "Event type such as tap or change. Defaults to tap."
          },
          "flowName": {
            "type": "string",
            "description": "ActionFlow name to bind."
          },
          "flow": { "type": "string", "description": "Alias for flowName." }
        },
        "required": ["flowName"]
      },
      "metadata": {
        "readOnly": false,
        "tags": ["ontology", "actionflow", "repair", "write"],
        "persona": "ide"
      }
    },
    {
      "name": "cogo.studio.qwenDesignSystemDecision",
      "description": "Studio WebMCP control-plane contract for Qwen design-system authority judgement. The browser registry exposes the bounded request shape; live Qwen calls are served by the configured WebMCP endpoint or headless proxy path.",
      "inputSchema": {
        "type": "object",
        "properties": {
          "schemaVersion": {
            "type": "string",
            "description": "Expected value: cogo.qwen-design-system-decision-params.v1."
          },
          "responseContract": {
            "type": "object",
            "description": "Closed decision response contract requested from Qwen."
          },
          "packet": {
            "type": "object",
            "description": "Bounded source-design-system decision packet with sourceContext/evidenceRefs."
          }
        },
        "required": ["schemaVersion", "responseContract", "packet"]
      },
      "metadata": {
        "readOnly": true,
        "tags": ["qwen", "ontology", "design-system", "judgement", "webmcp"],
        "persona": "all"
      }
    },
    {
      "name": "cogo.commerce.product.updatePrice",
      "description": "Update a product's price. Gated to Commerce shell. Phase 4 stub — the call is acknowledged but the backend Commerce API is out of scope.",
      "inputSchema": {
        "type": "object",
        "properties": {
          "productId": {
            "type": "string",
            "description": "Commerce product id."
          },
          "price": {
            "type": "number",
            "description": "New price in minor units (e.g. cents). Must be >= 0."
          }
        },
        "required": ["productId", "price"]
      },
      "metadata": {
        "readOnly": false,
        "tags": ["commerce", "write"],
        "persona": "commerce"
      }
    }
  ]
}
